import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaCheck, FaClock, FaTimes } from 'react-icons/fa';
import { adminLogin, getAdminUsers, getAdminStats, getAdminWhatsAppOperations, getUserStats, getUserData, createUser, deleteUser, getPendingUsers, approveUser, rejectUser, waitlistUser, getTrailRegistrations, importUserFromSheet, bulkImportFromSheets, getAdminRegistrationSettings, putAdminRegistrationSettings } from '../utils/api';
import './AdminDashboard.css';

const MAX_ORG_NAME_CHARS = 28;

function maskPhone(phone) {
  const s = String(phone || '').replace(/\s/g, '');
  if (s.length <= 4) return '••••';
  return `${s.slice(0, 3)}…${s.slice(-4)}`;
}

function formatAdminField(value) {
  const s = value == null ? '' : String(value).trim();
  return s || '—';
}

function normalizePendingUsersResponse(payload) {
  if (payload && Array.isArray(payload.ready_for_approval)) {
    return {
      ready: payload.ready_for_approval,
      awaitingEmail: payload.awaiting_email_verification || [],
      legacy: payload.legacy_unverified || [],
    };
  }
  if (payload && Array.isArray(payload.users)) {
    return { ready: payload.users, awaitingEmail: [], legacy: [] };
  }
  if (Array.isArray(payload)) {
    return { ready: payload, awaitingEmail: [], legacy: [] };
  }
  return { ready: [], awaitingEmail: [], legacy: [] };
}

function formatAdminDate(dateStr) {
  if (!dateStr) return '—';
  let dateString = String(dateStr).trim();
  if (!dateString.includes('Z') && !dateString.includes('+') && dateString.includes(' ')) {
    dateString = dateString.replace(' ', 'T') + 'Z';
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function isVisitWithinDays(user, days) {
  if (!user?.last_visit_date) return false;
  const t = new Date(user.last_visit_date).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= days * 24 * 60 * 60 * 1000;
}

function isCreatedWithinDays(user, days) {
  if (!user?.created_at) return false;
  const t = new Date(user.created_at).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= days * 24 * 60 * 60 * 1000;
}

/** Account state for admin table: legacy / API may send mixed case */
function getUserApprovalState(user) {
  const raw = user?.approval_status;
  if (raw == null || String(raw).trim() === '') return 'approved';
  const a = String(raw).trim().toLowerCase();
  if (a === 'pending') return 'pending';
  if (a === 'waitlist') return 'waitlist';
  if (a === 'rejected') return 'rejected';
  return 'approved';
}

/**
 * Row status for admin table. Rejected registrations are shown as "مرفوض" even if
 * deactivated_at is set later; soft-deactivated approved users show as معطّل.
 */
function getAdminRowAccountStatus(user) {
  if (getUserApprovalState(user) === 'rejected') return 'rejected';
  if (user?.deactivated_at) return 'deactivated';
  if (getUserApprovalState(user) === 'waitlist') return 'waitlist';
  if (getUserApprovalState(user) === 'pending') return 'pending';
  return 'active';
}

function userSortValue(user, key) {
  switch (key) {
    case 'id':
      return Number(user.id) || 0;
    case 'name':
      return String(user.name || '').toLowerCase();
    case 'phone':
      return String(user.phone_number || '');
    case 'role':
      return String(user.role || 'owner').toLowerCase();
    case 'tenant':
      return Number(user.org_owner_user_id != null ? user.org_owner_user_id : user.id) || 0;
    case 'created_at':
      return new Date(user.created_at || 0).getTime() || 0;
    case 'last_visit':
      return new Date(user.last_visit_date || 0).getTime() || 0;
    case 'status': {
      const s = getAdminRowAccountStatus(user);
      if (s === 'deactivated') return 4;
      if (s === 'rejected') return 3;
      if (s === 'waitlist') return 2.5;
      if (s === 'pending') return 2;
      return 1;
    }
    default:
      return 0;
  }
}

function SortTh({ colKey, sortConfig, onSort, className, children, language }) {
  const active = sortConfig.key === colKey;
  const arrow = active ? (sortConfig.dir === 'asc' ? '↑' : '↓') : '';
  return (
    <th
      scope="col"
      className={`admin-th-sortable ${className || ''}`.trim()}
      aria-sort={active ? (sortConfig.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(colKey)}
      title={language === 'ar' ? 'ترتيب العمود' : 'Sort by this column'}
    >
      <span className="admin-th-sortable__inner">
        <span className="admin-th-label-block">{children}</span>
        <span className={`admin-sort-ind${active ? ' admin-sort-ind--active' : ''}`} aria-hidden>
          {arrow || '↕'}
        </span>
      </span>
    </th>
  );
}

function KpiDrawerBody({ drawerKey, platformStats, platformInsights, language }) {
  const isAr = language === 'ar';
  if (drawerKey.startsWith('insight_')) {
    const id = drawerKey.replace('insight_', '');
    const row = platformInsights.find((r) => r.k === id);
    if (!row) return null;
    return (
      <>
        <h4 className="admin-kpi-drawer__title">{isAr ? row.labelAr : row.labelEn}</h4>
        <p className="admin-kpi-drawer__metric">{row.value}</p>
        <p className="admin-kpi-drawer__text">{isAr ? row.hintAr : row.hintEn}</p>
      </>
    );
  }
  const map = {
    wa_total: {
      title: isAr ? 'محادثات واتساب (إجمالي)' : 'WhatsApp conversations (total)',
      n: platformStats.totalConversations ?? '—',
      body: isAr
        ? 'كل خيوط المحادثة المسجّلة عبر المنصة. يعكس حجم قناة التواصل الفعلي.'
        : 'All conversation threads stored on the platform — reflects real messaging volume.',
    },
    wa_active7: {
      title: isAr ? 'محادثات بنشاط خلال 7 أيام' : 'Conversations active in last 7 days',
      n: platformStats.conversationsActive7d ?? '—',
      body: isAr
        ? 'محادثات لها نشاط أو رسالة خلال الأسبوع الماضي.'
        : 'Threads with activity or a message in the last week.',
    },
    wa_lines: {
      title: isAr ? 'خطوط واتساب متصلة' : 'Connected WhatsApp lines',
      n: platformStats.connectedWhatsAppLines ?? '—',
      body: isAr
        ? 'حسابات واتساب للأعمال بحالة «متصل» — جاهزة لاستقبال وإرسال الرسائل.'
        : 'Business WhatsApp integrations in connected status.',
    },
    wa_msgs7: {
      title: isAr ? 'رسائل واتساب (7 أيام)' : 'WhatsApp messages (7 days)',
      n: platformStats.whatsappMessagesLast7d ?? '—',
      body: isAr
        ? 'عدد رسائل المنصة المسجّلة خلال آخر أسبوع (وارد وصادر).'
        : 'Platform message rows logged in the last 7 days (inbound + outbound).',
    },
    units_total: {
      title: isAr ? 'وحدات عقارية (إجمالي)' : 'Property units (total)',
      n: platformStats.totalUnits ?? '—',
      body: isAr
        ? 'سجلات الوحدات في نظام المخزون/الوحدات عبر المنصة.'
        : 'Unit records in the inventory system across all tenants.',
    },
    campaigns_total: {
      title: isAr ? 'حملات بث مسجّلة' : 'Broadcast campaigns recorded',
      n: platformStats.totalBroadcastCampaigns ?? '—',
      body: isAr
        ? 'عدد حملات البث المنشأة في النظام (لا يعكس الرسائل المسلّمة فقط).'
        : 'Campaign records created in the system (not delivery success by itself).',
    },
    clients: {
      title: isAr ? 'إجمالي سجلات العملاء' : 'Total client records',
      n: platformStats.totalClients,
      body: isAr
        ? 'عدد صفوف العملاء عبر كل حسابات المنصة. لا يعرض هذا القائمة الكاملة — لفتح عملاء مستخدم معيّن اختر «بيانات» بجانب صفه.'
        : 'Count of client rows across all accounts. Open Data on a user row for that tenant’s clients.',
    },
    leads: {
      title: isAr ? 'إجمالي الفرص' : 'Total leads',
      n: platformStats.totalLeads,
      body: isAr
        ? 'جميع فرص البيع المسجّلة. التفاصيل حسب المستخدم من زر «بيانات».'
        : 'All pipeline leads. Per-user breakdown via Data on each row.',
    },
    deals: {
      title: isAr ? 'إجمالي الصفقات' : 'Total deals',
      n: platformStats.totalDeals,
      body: isAr
        ? 'صفقات مسجّلة على مستوى المنصة. للتحقق من حساب محدد استخدم «إحصائيات» أو «بيانات».'
        : 'Deals platform-wide. For one account use Stats or Data.',
    },
    activities: {
      title: isAr ? 'إجمالي الأنشطة' : 'Total activities',
      n: platformStats.totalActivities,
      body: isAr
        ? 'سجل النشاط مجمّعاً. مؤشر حجم الاستخدام لا جودة كل نشاط.'
        : 'Activity log volume — indicates usage scale, not per-action quality.',
    },
  };
  const block = map[drawerKey];
  if (!block) return null;
  return (
    <>
      <h4 className="admin-kpi-drawer__title">{block.title}</h4>
      <p className="admin-kpi-drawer__metric">{block.n}</p>
      <p className="admin-kpi-drawer__text">{block.body}</p>
    </>
  );
}

function OrgCell({ user, language }) {
  const orgId = user.org_owner_user_id != null ? user.org_owner_user_id : user.id;
  const rawName = (user.org_owner_name && String(user.org_owner_name).trim()) || '';
  const isOwner = (user.role || '') === 'owner';
  const displayName =
    rawName.length > MAX_ORG_NAME_CHARS ? `${rawName.slice(0, MAX_ORG_NAME_CHARS)}…` : rawName || `ID ${orgId}`;
  const fullTitle = [
    language === 'ar' ? `معرّف المنشأة: ${orgId}` : `Tenant owner user id: ${orgId}`,
    rawName ? `${language === 'ar' ? 'اسم الحساب' : 'Account'}: ${rawName}` : '',
    isOwner
      ? language === 'ar'
        ? 'هذا المستخدم هو مالك المنشأة'
        : 'This user is the organization owner'
      : language === 'ar'
        ? 'عضو في فريق هذه المنشأة'
        : 'Member of this organization team',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div className="org-cell org-cell--centered" title={fullTitle}>
      <div className="org-cell__top">
        <span className="org-cell__id">#{orgId}</span>
        {isOwner ? (
          <span className="admin-badge admin-badge--owner">{language === 'ar' ? 'مالك' : 'Owner'}</span>
        ) : (
          <span className="admin-badge admin-badge--member">{language === 'ar' ? 'عضو' : 'Member'}</span>
        )}
      </div>
      {rawName ? (
        <div className="org-cell__name">{displayName}</div>
      ) : (
        <div className="org-cell__name org-cell__name--muted">
          {language === 'ar' ? 'بدون اسم' : 'No name'}
        </div>
      )}
    </div>
  );
}

const AdminDashboard = ({ language = 'ar' }) => {
  const isRTL = language === 'ar';
  const [adminToken, setAdminToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dashboardRefreshing, setDashboardRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', phone_number: '', password: '' });
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');       // 'all' | 'owner' | 'manager' | 'employee'
  const [filterActivity, setFilterActivity] = useState('all'); // 'all' | '1' | '7' | '30' | 'inactive30'
  const [filterRegPeriod, setFilterRegPeriod] = useState('all'); // 'all' | '7' | '30' | '90'
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [revealPhones, setRevealPhones] = useState(() => sessionStorage.getItem('admin_reveal_phones') === '1');
  const [sortConfig, setSortConfig] = useState({ key: 'id', dir: 'desc' });
  /** Drill-down filter on user directory (null = no drill) */
  const [kpiDrill, setKpiDrill] = useState(null);
  /** Summary drawer for platform totals & insights (not row-filterable) */
  const [kpiDrawer, setKpiDrawer] = useState(null);
  const [pendingReady, setPendingReady] = useState([]);
  const [pendingAwaitingEmail, setPendingAwaitingEmail] = useState([]);
  const [pendingLegacy, setPendingLegacy] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [expandedPendingRows, setExpandedPendingRows] = useState(() => new Set());
  const [trailRegistrations, setTrailRegistrations] = useState([]);
  const [trailLoading, setTrailLoading] = useState(false);
  /** Sheet fetch diagnostics (row counts) when server returns them */
  const [trailFetchMeta, setTrailFetchMeta] = useState(null);
  const [regSettings, setRegSettings] = useState(null);
  const [regSettingsSaving, setRegSettingsSaving] = useState(false);
  const [whatsappOps, setWhatsappOps] = useState(null);
  const [whatsappOpsLoading, setWhatsappOpsLoading] = useState(false);
  const [whatsappOpsError, setWhatsappOpsError] = useState('');
  const usersSectionRef = useRef(null);
  const pendingAutoSelectRef = useRef(false);
  /** Main console areas: control deck first, registration hub, then user table */
  const [adminTab, setAdminTab] = useState('control');
  /** Inside Registration: settings form vs queues */
  const [regSubTab, setRegSubTab] = useState('settings');

  const scrollToUsers = useCallback(() => {
    setAdminTab('users');
    requestAnimationFrame(() => {
      usersSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  useEffect(() => {
    sessionStorage.setItem('admin_reveal_phones', revealPhones ? '1' : '0');
  }, [revealPhones]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Admin';
    return () => {
      document.title = prev;
    };
  }, []);

  const loadData = useCallback(async (token, { isManualRefresh = false } = {}) => {
    if (isManualRefresh) {
      setDashboardRefreshing(true);
      setError('');
    }
    try {
      const usersRes = await getAdminUsers(token);
      setUsers(usersRes.data.users);
      const statsRes = await getAdminStats(token);
      setPlatformStats(statsRes.data.stats);
      const pendingRes = await getPendingUsers(token);
      const pendingParsed = normalizePendingUsersResponse(pendingRes.data);
      setPendingReady(pendingParsed.ready);
      setPendingAwaitingEmail(pendingParsed.awaitingEmail);
      setPendingLegacy(pendingParsed.legacy);
      // Load trail registrations
      try {
        const trailRes = await getTrailRegistrations(token);
        setTrailRegistrations(trailRes.data.data || []);
        setTrailFetchMeta(trailRes.data._meta || null);
      } catch (err) {
        console.warn('Could not load trail registrations:', err.message);
        setTrailRegistrations([]);
        setTrailFetchMeta(null);
      }
      try {
        const regRes = await getAdminRegistrationSettings(token);
        setRegSettings(regRes.data);
      } catch (err) {
        console.warn('Could not load registration settings:', err.message);
        setRegSettings(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      if (isManualRefresh) setDashboardRefreshing(false);
    }
  }, []);

  const loadWhatsappOps = useCallback(async (token) => {
    const t = String(token || '').trim();
    if (!t) return;
    setWhatsappOpsLoading(true);
    setWhatsappOpsError('');
    try {
      const res = await getAdminWhatsAppOperations(t);
      setWhatsappOps(res.data);
    } catch (err) {
      setWhatsappOpsError(err.response?.data?.error || 'Failed to load WhatsApp operations');
      setWhatsappOps(null);
    } finally {
      setWhatsappOpsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminTab !== 'whatsapp_ops' || !isAuthenticated) return;
    const t = (sessionStorage.getItem('admin_token') || adminToken || '').trim();
    if (t) loadWhatsappOps(t);
  }, [adminTab, isAuthenticated, adminToken, loadWhatsappOps]);

  useEffect(() => {
    const savedToken = (sessionStorage.getItem('admin_token') || '').trim();
    if (savedToken) {
      setAdminToken(savedToken);
      setIsAuthenticated(true);
      loadData(savedToken);
    }
  }, [loadData]);

  const pendingRegTotal = pendingReady.length + pendingAwaitingEmail.length + pendingLegacy.length;

  useEffect(() => {
    if (pendingRegTotal === 0 || pendingAutoSelectRef.current) return;
    pendingAutoSelectRef.current = true;
    setAdminTab('registration');
    setRegSubTab('pending');
  }, [pendingRegTotal]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmed = String(adminToken || '').trim();
      const response = await adminLogin(trimmed);
      if (response.data.admin_token) {
        const canonical = String(response.data.admin_token).trim();
        sessionStorage.setItem('admin_token', canonical);
        setAdminToken(canonical);
        setIsAuthenticated(true);
        loadData(canonical);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid admin token');
    } finally {
      setLoading(false);
    }
  };

  const loadUserStats = async (userId) => {
    try {
      const response = await getUserStats(adminToken, userId);
      setAdminTab('users');
      setUserStats(response.data.stats);
      setSelectedUser(userId);
      setUserData(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load user stats');
    }
  };

  const loadUserData = async (userId) => {
    try {
      const response = await getUserData(adminToken, userId);
      setAdminTab('users');
      setUserData(response.data);
      setSelectedUser(userId);
      setUserStats(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load user data');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await createUser(adminToken, newUser);
      setNewUser({ name: '', phone_number: '', password: '' });
      setShowAddUser(false);
      loadData(adminToken);
      const reactivated = String(response.data?.message || '').toLowerCase().includes('reactivated');
      alert(
        reactivated
          ? language === 'ar'
            ? 'تمت إعادة تفعيل المستخدم بنفس رقم الهاتف (كلمة مرور جديدة).'
            : 'User reactivated with this phone number (new password).'
          : language === 'ar'
            ? 'تم إنشاء المستخدم بنجاح'
            : 'User created successfully'
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (
      !window.confirm(
      language === 'ar' 
          ? `تعطيل المستخدم "${userName}"؟ لن يستطيع تسجيل الدخول، وستبقى بياناته (عملاء، محادثات، إلخ) في النظام. يمكن إعادة تفعيله لاحقاً بنفس رقم الهاتف من «مستخدم جديد».`
          : `Deactivate user "${userName}"? They cannot sign in; their data (clients, conversations, etc.) stays in the system. You can reactivate later with the same phone via Add user.`
      )
    ) {
      return;
    }
    try {
      await deleteUser(adminToken, userId);
      loadData(adminToken);
      setSelectedUser(null);
      setUserStats(null);
      setUserData(null);
      alert(
        language === 'ar'
          ? 'تم تعطيل المستخدم — لا يمكنه تسجيل الدخول بعد الآن.'
          : 'User deactivated — they can no longer sign in.'
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const handleApproveUser = async (userId, userName) => {
    if (!window.confirm(
      language === 'ar' 
        ? `الموافقة على تسجيل "${userName}"؟`
        : `Approve registration for "${userName}"?`
    )) return;

    setPendingLoading(true);
    try {
      const res = await approveUser(adminToken, userId);
      loadData(adminToken);
      if (res.data?.email_sent === false) {
        alert(
          language === 'ar'
            ? res.data?.email_error
              ? `تمت الموافقة لكن فشل إرسال بريد التفعيل: ${res.data.email_error}`
              : 'تمت الموافقة — لا يوجد بريد لإرسال رسالة التفعيل.'
            : res.data?.email_error
              ? `Approved, but activation email failed: ${res.data.email_error}`
              : 'Approved — no email address to send activation message.'
        );
      } else {
        alert(
          language === 'ar'
            ? 'تمت الموافقة وإرسال بريد التفعيل مع تعليمات تسجيل الدخول.'
            : 'Approved — activation email sent with sign-in instructions.'
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve user');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleRejectUser = async (userId, userName) => {
    if (!window.confirm(
      language === 'ar' 
        ? `رفض تسجيل "${userName}"؟ (سبام/بيانات وهمية) — لن تُرسل أي رسالة تلقائية.`
        : `Reject "${userName}"? (spam/fake data) — no email will be sent.`
    )) return;

    setPendingLoading(true);
    try {
      await rejectUser(adminToken, userId);
      loadData(adminToken);
      alert(
        language === 'ar' 
          ? 'تم الرفض — لم يُرسل إشعار للمستخدم.'
          : 'Rejected — the applicant was not notified.'
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject user');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleWaitlistUser = async (userId, userName) => {
    if (!window.confirm(
      language === 'ar'
        ? `وضع "${userName}" في قائمة الانتظار؟ سيتم إرسال رسالة مهذّبة إلى بريده.`
        : `Add "${userName}" to the waitlist? A polite email will be sent to their address.`
    )) return;

    setPendingLoading(true);
    try {
      const res = await waitlistUser(adminToken, userId);
      loadData(adminToken);
      if (res.data?.email_sent === false) {
        alert(
          language === 'ar'
            ? res.data?.email_error
              ? `تمت قائمة الانتظار لكن فشل إرسال البريد: ${res.data.email_error}`
              : 'تمت قائمة الانتظار — لا يوجد بريد لإرسال إشعار.'
            : res.data?.email_error
              ? `Waitlisted, but email failed: ${res.data.email_error}`
              : 'Waitlisted — no email address to notify.'
        );
      } else {
        alert(
          language === 'ar'
            ? 'تم وضع الطلب في قائمة الانتظار وإرسال البريد.'
            : 'Moved to waitlist and notification email sent.'
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to waitlist user');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleImportFromSheet = async (userData) => {
    setTrailLoading(true);
    try {
      await importUserFromSheet(adminToken, userData);
      loadData(adminToken);
      alert(
        language === 'ar'
          ? 'تم استيراد المستخدم بنجاح من نموذج التسجيل'
          : 'User imported successfully from registration form'
      );
    } catch (err) {
      if (err.response?.status === 409) {
        alert(
          language === 'ar'
            ? 'المستخدم موجود مسبقاً في النظام'
            : 'User already exists in the system'
        );
      } else {
        setError(err.response?.data?.error || 'Failed to import user');
      }
    } finally {
      setTrailLoading(false);
    }
  };

  const handleBulkImport = async () => {
    const newRegistrations = trailRegistrations.filter(r => !r.exists_in_real);
    
    if (newRegistrations.length === 0) {
      alert(
        language === 'ar'
          ? 'لا توجد تسجيلات جديدة للاستيراد'
          : 'No new registrations to import'
      );
      return;
    }

    if (!window.confirm(
      language === 'ar'
        ? `استيراد ${newRegistrations.length} تسجيل جديد إلى Real؟`
        : `Import ${newRegistrations.length} new registrations to Real?`
    )) return;

    setTrailLoading(true);
    try {
      const res = await bulkImportFromSheets(adminToken, newRegistrations);
      loadData(adminToken);
      alert(
        language === 'ar'
          ? `تم الاستيراد بنجاح: ${res.data.results.success.length} مستخدم`
          : `Import successful: ${res.data.results.success.length} users imported`
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Bulk import failed');
    } finally {
      setTrailLoading(false);
    }
  };

  const handleSaveRegistrationSettings = async (e) => {
    e?.preventDefault?.();
    if (!regSettings) return;
    setRegSettingsSaving(true);
    setError('');
    try {
      await putAdminRegistrationSettings(adminToken, {
        public_signup_enabled: regSettings.public_signup_enabled !== false,
        signup_closed_message_ar: regSettings.signup_closed_message_ar || '',
        signup_closed_message_en: regSettings.signup_closed_message_en || '',
        rollout_phase: regSettings.rollout_phase || '',
        target_go_live_at: regSettings.target_go_live_at || null,
        internal_notes: regSettings.internal_notes || '',
      });
      await loadData(adminToken);
      alert(language === 'ar' ? 'تم حفظ إعدادات التسجيل' : 'Registration settings saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save registration settings');
    } finally {
      setRegSettingsSaving(false);
    }
  };

  const handleRefreshDashboard = useCallback(() => {
    const t = sessionStorage.getItem('admin_token') || adminToken;
    if (!t || dashboardRefreshing) return;
    loadData(t, { isManualRefresh: true });
    if (adminTab === 'whatsapp_ops') loadWhatsappOps(t);
  }, [adminToken, dashboardRefreshing, loadData, adminTab, loadWhatsappOps]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setAdminToken('');
    setUsers([]);
    setPlatformStats(null);
    setSelectedUser(null);
    setUserStats(null);
    setUserData(null);
    setWhatsappOps(null);
    setWhatsappOpsError('');
  };

  const userStatusCounts = useMemo(() => {
    let active = 0;
    let pending = 0;
    let waitlist = 0;
    let rejected = 0;
    let deactivated = 0;
    for (const u of users) {
      if (getUserApprovalState(u) === 'rejected') {
        rejected += 1;
        continue;
      }
      if (u.deactivated_at) {
        deactivated += 1;
        continue;
      }
      if (getUserApprovalState(u) === 'waitlist') waitlist += 1;
      else if (getUserApprovalState(u) === 'pending') pending += 1;
      else active += 1;
    }
    return { active, pending, waitlist, rejected, deactivated };
  }, [users]);

  const filteredUsers = useMemo(() => {
    let list = users;

    if (!showDeactivated) {
      list = list.filter((u) => !u.deactivated_at);
    }

    // Text search
    if (userSearchQuery.trim()) {
      const q = userSearchQuery.toLowerCase();
      list = list.filter(
        (u) =>
          (u.name || '').toLowerCase().includes(q) ||
          (u.phone_number || '').includes(userSearchQuery) ||
          String(u.id || '').includes(userSearchQuery) ||
          String(u.owner_id || '').includes(userSearchQuery) ||
          String(u.org_owner_user_id || '').includes(userSearchQuery) ||
          (u.org_owner_name || '').toLowerCase().includes(q)
      );
    }

    // Role filter
    if (filterRole !== 'all') {
      list = list.filter((u) => (u.role || 'owner') === filterRole);
    }

    // Activity filter
    if (filterActivity !== 'all') {
      if (filterActivity === 'inactive30') {
        list = list.filter((u) => !isVisitWithinDays(u, 30));
      } else {
        const days = parseInt(filterActivity, 10);
        list = list.filter((u) => isVisitWithinDays(u, days));
      }
    }

    // Registration period filter
    if (filterRegPeriod !== 'all') {
      const days = parseInt(filterRegPeriod, 10);
      list = list.filter((u) => isCreatedWithinDays(u, days));
    }

    return list;
  }, [users, showDeactivated, userSearchQuery, filterRole, filterActivity, filterRegPeriod]);

  const usersAfterDrill = useMemo(() => {
    if (!kpiDrill) return filteredUsers;
    if (kpiDrill === 'owners') {
      return filteredUsers.filter((u) => (u.role || '') === 'owner');
    }
    if (kpiDrill === 'staff') {
      return filteredUsers.filter((u) => {
        const r = u.role || '';
        return r === 'employee' || r === 'manager';
      });
    }
    if (kpiDrill === 'active7') {
      return filteredUsers.filter((u) => isVisitWithinDays(u, 7));
    }
    if (kpiDrill === 'new30') {
      return filteredUsers.filter((u) => isCreatedWithinDays(u, 30));
    }
    return filteredUsers;
  }, [filteredUsers, kpiDrill]);

  const sortedDisplayUsers = useMemo(() => {
    const list = [...usersAfterDrill];
    const { key, dir } = sortConfig;
    list.sort((a, b) => {
      const va = userSortValue(a, key);
      const vb = userSortValue(b, key);
      let cmp = 0;
      if (typeof va === 'string' && typeof vb === 'string') {
        cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      } else {
        cmp = va < vb ? -1 : va > vb ? 1 : 0;
      }
      return dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [usersAfterDrill, sortConfig]);

  const toggleSort = (colKey) => {
    setSortConfig((prev) =>
      prev.key === colKey ? { key: colKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key: colKey, dir: 'asc' }
    );
  };

  const platformInsights = useMemo(() => {
    if (!platformStats) return [];
    const u = Math.max(1, Number(platformStats.totalUsers) || 0);
    const ownerCountRaw = platformStats.ownerAccounts;
    const ownerCount =
      ownerCountRaw !== undefined && ownerCountRaw !== null ? Number(ownerCountRaw) : null;
    const c = Number(platformStats.totalClients) || 0;
    const l = Number(platformStats.totalLeads) || 0;
    const d = Number(platformStats.totalDeals) || 0;
    const act = Number(platformStats.totalActivities) || 0;
    const staff = Number(platformStats.staffUsers) || 0;
    const active7 = Number(platformStats.activeUsersLast7Days) || 0;
    const pctActive = u ? Math.round((active7 / u) * 100) : 0;
    const staffPerOwner =
      ownerCount !== null && !Number.isNaN(ownerCount) && ownerCount > 0
        ? (staff / ownerCount).toFixed(1)
        : '—';
    return [
      {
        k: 'clients_per_user',
        labelAr: 'متوسط العملاء / مستخدم',
        labelEn: 'Avg clients / user',
        value: (c / u).toFixed(1),
        hintAr: 'حجم دفتر العملاء مقارنة بعدد الحسابات',
        hintEn: 'Operational depth vs account count',
      },
      {
        k: 'staff_per_owner',
        labelAr: 'موظفون تقريباً / مالك',
        labelEn: 'Staff / owner (avg)',
        value: staffPerOwner,
        hintAr: 'توزيع الفرق على المنشآت',
        hintEn: 'Team size per tenant owner',
      },
      {
        k: 'leads_per_user',
        labelAr: 'متوسط الفرص / مستخدم',
        labelEn: 'Avg leads / user',
        value: (l / u).toFixed(1),
        hintAr: 'خط الأنابيب لكل حساب',
        hintEn: 'Pipeline per account',
      },
      {
        k: 'act_per_deal',
        labelAr: 'أنشطة / صفقة',
        labelEn: 'Activities / deal',
        value: d ? (act / d).toFixed(1) : '—',
        hintAr: 'مدى النشاط حول الصفقات',
        hintEn: 'Engagement around closed/open deals',
      },
      {
        k: 'active_pct',
        labelAr: 'نشط خلال 7 أيام',
        labelEn: 'Active last 7 days',
        value: `${active7} (${pctActive}%)`,
        hintAr: 'مستخدمو last_visit في أسبوع',
        hintEn: 'Users with a visit in the last week',
      },
    ];
  }, [platformStats]);

  const regActionCount = useMemo(
    () =>
      pendingReady.length +
      pendingLegacy.length +
      trailRegistrations.filter((r) => r && !r.exists_in_real).length,
    [pendingReady, pendingLegacy, trailRegistrations]
  );

  const whatsappOpsCriticalCount = useMemo(
    () => (Array.isArray(whatsappOps?.criticalAlerts) ? whatsappOps.criticalAlerts.length : 0),
    [whatsappOps]
  );

  const displayPhone = useCallback(
    (phone) => (revealPhones ? phone || '—' : maskPhone(phone)),
    [revealPhones]
  );

  const togglePendingExpand = useCallback((rowKey) => {
    setExpandedPendingRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const renderPendingUserActions = (user, actionLabel) => {
    const approveLabel = language === 'ar' ? 'موافقة' : 'Approve';
    const waitlistLabel = language === 'ar' ? 'قائمة انتظار' : 'Waitlist';
    const rejectLabel = language === 'ar' ? 'رفض' : 'Reject';

    return (
      <div className="pending-user-actions pending-user-actions--table">
        <button
          type="button"
          onClick={() => handleApproveUser(user.id, actionLabel)}
          disabled={pendingLoading}
          className="btn btn-success pending-action-icon-btn"
          aria-label={approveLabel}
          title={approveLabel}
        >
          <FaCheck aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => handleWaitlistUser(user.id, actionLabel)}
          disabled={pendingLoading}
          className="btn btn-waitlist pending-action-icon-btn"
          aria-label={waitlistLabel}
          title={waitlistLabel}
        >
          <FaClock aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => handleRejectUser(user.id, actionLabel)}
          disabled={pendingLoading}
          className="btn btn-danger pending-action-icon-btn"
          aria-label={rejectLabel}
          title={rejectLabel}
        >
          <FaTimes aria-hidden />
        </button>
      </div>
    );
  };

  const renderPendingUsersTable = (
    rows,
    { queue, showActions = true, statusPillClass, statusLabel, dateLabel, getDateValue } = {}
  ) => {
    if (!rows.length) return null;
    const colCount = showActions ? 9 : 8;

    return (
      <div className="pending-users-table-wrap users-table-wrap">
        <table className="admin-data-table pending-users-table">
          <thead>
            <tr>
              <th scope="col" className="col-expand admin-th-static" aria-label={language === 'ar' ? 'توسيع' : 'Expand'} />
              <th scope="col" className="col-id admin-th-static">
                ID
              </th>
              <th scope="col" className="admin-th-static">
                {language === 'ar' ? 'الاسم' : 'Name'}
              </th>
              <th scope="col" className="admin-th-static">
                {language === 'ar' ? 'المنشأة' : 'Business'}
              </th>
              <th scope="col" className="admin-th-static">
                {language === 'ar' ? 'البريد' : 'Email'}
              </th>
              <th scope="col" className="col-phone admin-th-static">
                {language === 'ar' ? 'الهاتف' : 'Phone'}
              </th>
              <th scope="col" className="col-status admin-th-static">
                {language === 'ar' ? 'الحالة' : 'Status'}
              </th>
              <th scope="col" className="col-date admin-th-static">
                {dateLabel || (language === 'ar' ? 'التاريخ' : 'Date')}
              </th>
              {showActions && (
                <th scope="col" className="col-actions admin-th-static">
                  {language === 'ar' ? 'إجراءات' : 'Actions'}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((user) => {
              const rowKey = `${queue}-${user.id}`;
              const isExpanded = expandedPendingRows.has(rowKey);
              const displayName =
                user.name?.trim() ||
                user.business_name?.trim() ||
                (user.phone_number
                  ? displayPhone(user.phone_number)
                  : language === 'ar'
                    ? `طلب #${user.id}`
                    : `Request #${user.id}`);
              const actionLabel = user.name || user.business_name || user.phone_number || `#${user.id}`;
              const dateValue = getDateValue ? getDateValue(user) : formatAdminDate(user.created_at);

              return (
                <React.Fragment key={rowKey}>
                  <tr
                    className={[
                      'pending-users-table__row',
                      isExpanded ? 'pending-users-table__row--expanded' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={(e) => {
                      if (e.target.closest('button')) return;
                      togglePendingExpand(rowKey);
                    }}
                  >
                    <td className="col-expand">
                      <button
                        type="button"
                        className="pending-expand-btn"
                        onClick={() => togglePendingExpand(rowKey)}
                        aria-expanded={isExpanded}
                        aria-controls={`pending-detail-${rowKey}`}
                        title={isExpanded ? (language === 'ar' ? 'طي' : 'Collapse') : language === 'ar' ? 'توسيع' : 'Expand'}
                      >
                        <span className={`pending-expand-btn__chevron${isExpanded ? ' pending-expand-btn__chevron--open' : ''}`} aria-hidden>
                          ▶
                        </span>
                      </button>
                    </td>
                    <td className="col-id mono">{user.id ?? '—'}</td>
                    <td className="pending-users-table__name cell-strong">{displayName}</td>
                    <td>{formatAdminField(user.business_name)}</td>
                    <td className="pending-users-table__email">{formatAdminField(user.email)}</td>
                    <td className="col-phone mono" title={revealPhones ? undefined : user.phone_number}>
                      {displayPhone(user.phone_number)}
                    </td>
                    <td className="col-status">
                      <span className={`admin-status-pill ${statusPillClass || ''}`}>{statusLabel}</span>
                    </td>
                    <td className="col-date muted">{dateValue}</td>
                    {showActions && (
                      <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                        {renderPendingUserActions(user, actionLabel)}
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr id={`pending-detail-${rowKey}`} className="pending-users-table__detail-row">
                      <td colSpan={colCount}>
                        <div className="pending-users-table__detail-panel">
                          <dl className="pending-user-detail-list pending-user-detail-list--expanded">
                            <div className="pending-user-detail-row">
                              <dt>{language === 'ar' ? 'الاسم' : 'Name'}</dt>
                              <dd>{formatAdminField(user.name)}</dd>
                            </div>
                            <div className="pending-user-detail-row">
                              <dt>{language === 'ar' ? 'المنشأة' : 'Business'}</dt>
                              <dd>{formatAdminField(user.business_name)}</dd>
                            </div>
                            <div className="pending-user-detail-row">
                              <dt>{language === 'ar' ? 'البريد' : 'Email'}</dt>
                              <dd>{formatAdminField(user.email)}</dd>
                            </div>
                            <div className="pending-user-detail-row">
                              <dt>{language === 'ar' ? 'الهاتف' : 'Phone'}</dt>
                              <dd className="mono">{displayPhone(user.phone_number)}</dd>
                            </div>
                            {user.registration_source && (
                              <div className="pending-user-detail-row">
                                <dt>{language === 'ar' ? 'المصدر' : 'Source'}</dt>
                                <dd>{formatAdminField(user.registration_source)}</dd>
                              </div>
                            )}
                            {user.role && (
                              <div className="pending-user-detail-row">
                                <dt>{language === 'ar' ? 'الدور' : 'Role'}</dt>
                                <dd>{formatAdminField(user.role)}</dd>
                              </div>
                            )}
                            {user.language && (
                              <div className="pending-user-detail-row">
                                <dt>{language === 'ar' ? 'اللغة' : 'Language'}</dt>
                                <dd>{formatAdminField(user.language)}</dd>
                              </div>
                            )}
                            <div className="pending-user-detail-row">
                              <dt>{dateLabel || (language === 'ar' ? 'تاريخ الطلب' : 'Submitted')}</dt>
                              <dd>{dateValue}</dd>
                            </div>
                            {user.expires_at && (
                              <div className="pending-user-detail-row">
                                <dt>{language === 'ar' ? 'ينتهي الرابط' : 'Link expires'}</dt>
                                <dd>{formatAdminDate(user.expires_at)}</dd>
                              </div>
                            )}
                          </dl>
                          {showActions && (
                            <div className="pending-users-table__detail-actions">
                              {renderPendingUserActions(user, actionLabel)}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login-container" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="admin-login-card">
          <h1 className="admin-login-title">{language === 'ar' ? 'تسجيل دخول المشرف' : 'Admin sign-in'}</h1>
          <p className="admin-login-lead">
            {language === 'ar'
              ? 'وصول مقيّد — لا تشارك رمز الدخول ولا تعرض البيانات في أماكن عامة.'
              : 'Restricted access — do not share your token or display data in public.'}
          </p>
          <form onSubmit={handleAdminLogin}>
            <div className="form-group">
              <label>{language === 'ar' ? 'رمز المشرف' : 'Admin token'}</label>
              <input
                type="password"
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
                placeholder={language === 'ar' ? 'أدخل الرمز' : 'Enter token'}
                required
                autoComplete="off"
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" className="admin-button" disabled={loading}>
              {loading 
                ? language === 'ar'
                  ? 'جاري التحقق…'
                  : 'Verifying…'
                : language === 'ar'
                  ? 'دخول'
                  : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="admin-header">
        <div>
          <h1 className="admin-header__title">{language === 'ar' ? 'لوحة الإدارة' : 'Admin console'}</h1>
          <p className="admin-header__subtitle">
            {language === 'ar' ? 'مستخدمون وإحصائيات المنصة' : 'Users & platform metrics'}
          </p>
        </div>
        <div className="admin-header__actions">
          <button
            type="button"
            onClick={handleRefreshDashboard}
            className="admin-refresh-btn admin-refresh-btn--icon-only"
            disabled={dashboardRefreshing}
            aria-busy={dashboardRefreshing}
            aria-label={language === 'ar' ? 'تحديث' : 'Refresh'}
            title={language === 'ar' ? 'تحديث' : 'Refresh'}
          >
            ↻
          </button>
          <button type="button" onClick={handleLogout} className="logout-btn">
            {language === 'ar' ? 'خروج آمن' : 'Sign out'}
        </button>
      </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <nav
        className="admin-main-tabs"
        role="tablist"
        aria-label={language === 'ar' ? 'أقسام لوحة الإدارة' : 'Admin console sections'}
      >
        <button
          type="button"
          role="tab"
          id="admin-tab-control"
          aria-selected={adminTab === 'control'}
          className={`admin-main-tab${adminTab === 'control' ? ' admin-main-tab--active' : ''}`}
          onClick={() => setAdminTab('control')}
        >
          {language === 'ar' ? 'لوحة التحكم' : 'Control center'}
        </button>
        <button
          type="button"
          role="tab"
          id="admin-tab-users"
          aria-selected={adminTab === 'users'}
          className={`admin-main-tab${adminTab === 'users' ? ' admin-main-tab--active' : ''}`}
          onClick={() => setAdminTab('users')}
        >
          {language === 'ar' ? 'دليل المستخدمين' : 'User directory'}
        </button>
        <button
          type="button"
          role="tab"
          id="admin-tab-registration"
          aria-selected={adminTab === 'registration'}
          className={`admin-main-tab${adminTab === 'registration' ? ' admin-main-tab--active' : ''}`}
          onClick={() => {
            setAdminTab('registration');
            const hasTrailNew = trailRegistrations.some((r) => r && !r.exists_in_real);
            if (pendingReady.length > 0 || pendingAwaitingEmail.length > 0) setRegSubTab('pending');
            else if (hasTrailNew) setRegSubTab('trail');
            else setRegSubTab('settings');
          }}
        >
          <span className="admin-main-tab__label">
            {language === 'ar' ? 'التسجيل والإطلاق' : 'Registration & rollout'}
          </span>
          {regActionCount > 0 && (
            <span
              className="admin-main-tab__badge"
              title={language === 'ar' ? 'بانتظار إجراء' : 'Awaiting action'}
            >
              {regActionCount > 99 ? '99+' : regActionCount}
            </span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          id="admin-tab-whatsapp-ops"
          aria-selected={adminTab === 'whatsapp_ops'}
          className={`admin-main-tab${adminTab === 'whatsapp_ops' ? ' admin-main-tab--active' : ''}`}
          onClick={() => setAdminTab('whatsapp_ops')}
        >
          <span className="admin-main-tab__label">
            {language === 'ar' ? 'عمليات واتساب' : 'WhatsApp operations'}
          </span>
          {whatsappOpsCriticalCount > 0 && (
            <span
              className="admin-main-tab__badge admin-main-tab__badge--alert"
              title={language === 'ar' ? 'تنبيهات حرجة' : 'Critical alerts'}
            >
              {whatsappOpsCriticalCount > 99 ? '99+' : whatsappOpsCriticalCount}
            </span>
          )}
        </button>
      </nav>

      {adminTab === 'control' && platformStats && (
        <section
          className={`admin-control${dashboardRefreshing ? ' admin-control--refreshing' : ''}`}
          aria-labelledby="platform-stats-heading"
          id="admin-kpis"
          aria-busy={dashboardRefreshing}
        >
          <div className="admin-control__head">
            <div className="admin-control__head-text">
              <h2 id="platform-stats-heading" className="admin-control__title">
                {language === 'ar' ? 'لوحة التحكم' : 'Control center'}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleRefreshDashboard}
              className="admin-refresh-btn admin-refresh-btn--compact admin-refresh-btn--icon-only"
              disabled={dashboardRefreshing}
              aria-label={language === 'ar' ? 'تحديث' : 'Refresh'}
              title={language === 'ar' ? 'تحديث' : 'Refresh'}
            >
              ↻
            </button>
            </div>

          <div className="admin-kpi-primary">
            <button
              type="button"
              className={`admin-kpi-tile admin-kpi-tile--ink${kpiDrill === null && !userSearchQuery.trim() ? ' admin-kpi-tile--active' : ''}`}
              onClick={() => {
                setKpiDrill(null);
                setKpiDrawer(null);
                scrollToUsers();
              }}
            >
              <span className="admin-kpi-tile__label">{language === 'ar' ? 'حسابات المنصة' : 'Platform accounts'}</span>
              <span className="admin-kpi-tile__value">{platformStats.totalUsers}</span>
              <span className="admin-kpi-tile__hint">
                {language === 'ar' ? 'نشط (غير معطّل)' : 'Active (not deactivated)'}
                {Number(platformStats.deactivatedUsers) > 0
                  ? language === 'ar'
                    ? ` · معطّل: ${platformStats.deactivatedUsers}`
                    : ` · deactivated: ${platformStats.deactivatedUsers}`
                  : ''}
              </span>
            </button>
            <button
              type="button"
              className={`admin-kpi-tile admin-kpi-tile--teal${kpiDrill === 'owners' ? ' admin-kpi-tile--active' : ''}`}
              onClick={() => {
                setKpiDrill('owners');
                setKpiDrawer(null);
                scrollToUsers();
              }}
            >
              <span className="admin-kpi-tile__label">{language === 'ar' ? 'مالكو منشآت' : 'Tenant owners'}</span>
              <span className="admin-kpi-tile__value">{platformStats.ownerAccounts ?? '—'}</span>
              <span className="admin-kpi-tile__hint">{language === 'ar' ? 'تصفية الجدول' : 'Filter table'}</span>
            </button>
            <button
              type="button"
              className={`admin-kpi-tile admin-kpi-tile--slate${kpiDrill === 'staff' ? ' admin-kpi-tile--active' : ''}`}
              onClick={() => {
                setKpiDrill('staff');
                setKpiDrawer(null);
                scrollToUsers();
              }}
            >
              <span className="admin-kpi-tile__label">{language === 'ar' ? 'فريق العمل' : 'Team (staff)'}</span>
              <span className="admin-kpi-tile__value">{platformStats.staffUsers ?? '—'}</span>
              <span className="admin-kpi-tile__hint">{language === 'ar' ? 'موظفون ومديرون' : 'Employees & managers'}</span>
            </button>
            <button
              type="button"
              className={`admin-kpi-tile admin-kpi-tile--accent${kpiDrill === 'active7' ? ' admin-kpi-tile--active' : ''}`}
              onClick={() => {
                setKpiDrill('active7');
                setKpiDrawer(null);
                scrollToUsers();
              }}
            >
              <span className="admin-kpi-tile__label">{language === 'ar' ? 'نشطون (7 أيام)' : 'Active (7 days)'}</span>
              <span className="admin-kpi-tile__value">{platformStats.activeUsersLast7Days ?? '—'}</span>
              <span className="admin-kpi-tile__hint">{language === 'ar' ? 'حسب آخر زيارة' : 'By last visit'}</span>
            </button>
            </div>

          <div className="admin-kpi-growth">
            <button
              type="button"
              className={`admin-kpi-tile admin-kpi-tile--narrow${kpiDrill === 'new30' ? ' admin-kpi-tile--active' : ''}`}
              onClick={() => {
                setKpiDrill('new30');
                setKpiDrawer(null);
                scrollToUsers();
              }}
            >
              <span className="admin-kpi-tile__label">{language === 'ar' ? 'تسجيلات جديدة' : 'New sign-ups'}</span>
              <span className="admin-kpi-tile__value">{platformStats.newUsersLast30Days ?? 0}</span>
              <span className="admin-kpi-tile__hint">{language === 'ar' ? 'آخر 30 يوماً' : 'Last 30 days'}</span>
            </button>
            </div>

          <div className="admin-kpi-ops">
            <h3 className="admin-kpi-ops__title">{language === 'ar' ? 'نبض التشغيل (واتساب والمخزون)' : 'Operational pulse (WhatsApp & inventory)'}</h3>
            <p className="admin-kpi-ops__lead">
              {language === 'ar'
                ? 'أرقام مباشرة من قاعدة البيانات — تعكس استخدام المنصة التشغيلي وليس لوحة الحملات فقط.'
                : 'Live database totals — reflects operational usage, not only the Campaigns screen.'}
            </p>
            <div className="admin-kpi-ops__grid">
              {[
                { key: 'wa_total', v: platformStats.totalConversations, la: 'محادثات', en: 'Conversations' },
                { key: 'wa_active7', v: platformStats.conversationsActive7d, la: 'نشط 7ي', en: 'Active 7d' },
                { key: 'wa_lines', v: platformStats.connectedWhatsAppLines, la: 'خطوط WA', en: 'WA lines' },
                { key: 'wa_msgs7', v: platformStats.whatsappMessagesLast7d, la: 'رسائل 7ي', en: 'Msgs 7d' },
                { key: 'units_total', v: platformStats.totalUnits, la: 'وحدات', en: 'Units' },
                { key: 'campaigns_total', v: platformStats.totalBroadcastCampaigns, la: 'حملات', en: 'Campaigns' },
              ].map((row) => (
                <button
                  key={row.key}
                  type="button"
                  className={`admin-kpi-op${kpiDrawer === row.key ? ' admin-kpi-op--open' : ''}`}
                  onClick={() => setKpiDrawer((d) => (d === row.key ? null : row.key))}
                >
                  <span className="admin-kpi-op__value">{row.v ?? '—'}</span>
                  <span className="admin-kpi-op__label">{language === 'ar' ? row.la : row.en}</span>
                </button>
              ))}
            </div>
            </div>

          <div className="admin-kpi-operations">
            <h3 className="admin-kpi-operations__title">
              {language === 'ar' ? 'بيانات التشغيل (عبر المنصة)' : 'Operations data (cross-tenant)'}
            </h3>
            <p className="admin-kpi-operations__lead">
              {language === 'ar'
                ? 'إجماليات على كل المنشآت. للتفاصيل حسب الحساب استخدم «بيانات» في الجدول أدناه.'
                : 'Totals across all tenants. Per-account detail: use Data on a user row below.'}
            </p>
            <div className="admin-kpi-operations__grid">
              {[
                { key: 'clients', v: platformStats.totalClients, la: 'عملاء', en: 'Clients' },
                { key: 'leads', v: platformStats.totalLeads, la: 'فرص', en: 'Leads' },
                { key: 'deals', v: platformStats.totalDeals, la: 'صفقات', en: 'Deals' },
                { key: 'activities', v: platformStats.totalActivities, la: 'أنشطة', en: 'Activities' },
              ].map((row) => (
                <button
                  key={row.key}
                  type="button"
                  className={`admin-kpi-mini${kpiDrawer === row.key ? ' admin-kpi-mini--open' : ''}`}
                  onClick={() => setKpiDrawer((d) => (d === row.key ? null : row.key))}
                >
                  <span className="admin-kpi-mini__value">{row.v}</span>
                  <span className="admin-kpi-mini__label">{language === 'ar' ? row.la : row.en}</span>
                </button>
              ))}
          </div>
        </div>

          {platformInsights.length > 0 && (
            <details className="admin-insights-fold">
              <summary className="admin-insights-fold__summary">
                {language === 'ar' ? 'إشارات جودة واستخدام (مشتقة)' : 'Quality & usage signals (derived)'}
              </summary>
              <p className="admin-insights-fold__lead">
                {language === 'ar'
                  ? 'نِسَب تُحسب من الإجماليات — للمراقبة فقط.'
                  : 'Ratios from totals — monitoring only, not SLA.'}
              </p>
              <div className="admin-insights-fold__grid">
                {platformInsights.map((row) => (
                  <button
                    key={row.k}
                    type="button"
                    className="admin-insights-chip"
                    onClick={() => setKpiDrawer((d) => (d === `insight_${row.k}` ? null : `insight_${row.k}`))}
                    title={language === 'ar' ? row.hintAr : row.hintEn}
                  >
                    <span className="admin-insights-chip__val">{row.value}</span>
                    <span className="admin-insights-chip__lab">{language === 'ar' ? row.labelAr : row.labelEn}</span>
                  </button>
                ))}
              </div>
            </details>
          )}

          {kpiDrawer && (
            <div className="admin-kpi-drawer" role="region" aria-live="polite">
              <div className="admin-kpi-drawer__inner">
                <button type="button" className="admin-kpi-drawer__close" onClick={() => setKpiDrawer(null)} aria-label="Close">
                  ×
                </button>
                <KpiDrawerBody drawerKey={kpiDrawer} platformStats={platformStats} platformInsights={platformInsights} language={language} />
                <button type="button" className="admin-kpi-drawer__cta" onClick={() => { setKpiDrawer(null); scrollToUsers(); }}>
                  {language === 'ar' ? 'الانتقال إلى دليل المستخدمين' : 'Go to user directory'}
                </button>
          </div>
        </div>
          )}
        </section>
      )}

      {adminTab === 'users' && (
      <section ref={usersSectionRef} className="users-section" aria-labelledby="users-heading" id="admin-users">
        <div className="section-header">
          <h2 id="users-heading" className="admin-section-title">
            {language === 'ar' ? 'دليل المستخدمين' : 'User directory'}
          </h2>
          <div className="admin-toolbar">
            <input
              type="search"
              className="admin-search"
              placeholder={language === 'ar' ? 'بحث: اسم، هاتف، معرّف…' : 'Search: name, phone, id…'}
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              aria-label={language === 'ar' ? 'بحث المستخدمين' : 'Search users'}
            />
            <button type="button" onClick={() => setShowAddUser(!showAddUser)} className="add-user-btn">
              {language === 'ar' ? '+ مستخدم' : '+ Add user'}
            </button>
          </div>

          {/* Filter bar */}
          <div className="admin-filter-bar">
            {/* Role */}
            <div className="admin-filter-group">
              <span className="admin-filter-label">{language === 'ar' ? 'الدور' : 'Role'}</span>
              {[
                { v: 'all',      en: 'All',      ar: 'الكل' },
                { v: 'owner',    en: 'Owner',     ar: 'مالك' },
                { v: 'manager',  en: 'Manager',   ar: 'مدير' },
                { v: 'employee', en: 'Employee',  ar: 'موظف' },
              ].map(opt => (
                <button key={opt.v} type="button"
                  className={`admin-filter-chip${filterRole === opt.v ? ' active' : ''}`}
                  onClick={() => setFilterRole(opt.v)}>
                  {language === 'ar' ? opt.ar : opt.en}
                </button>
              ))}
        </div>

            {/* Activity */}
            <div className="admin-filter-group">
              <span className="admin-filter-label">{language === 'ar' ? 'النشاط' : 'Activity'}</span>
              {[
                { v: 'all',        en: 'All',           ar: 'الكل' },
                { v: '1',          en: 'Today',         ar: 'اليوم' },
                { v: '7',          en: 'Last 7 days',   ar: '7 أيام' },
                { v: '30',         en: 'Last 30 days',  ar: '30 يوم' },
                { v: 'inactive30', en: 'Inactive 30d+', ar: 'غير نشط +30' },
              ].map(opt => (
                <button key={opt.v} type="button"
                  className={`admin-filter-chip${filterActivity === opt.v ? ' active' : ''}`}
                  onClick={() => setFilterActivity(opt.v)}>
                  {language === 'ar' ? opt.ar : opt.en}
                </button>
              ))}
            </div>

            {/* Registration */}
            <div className="admin-filter-group">
              <span className="admin-filter-label">{language === 'ar' ? 'التسجيل' : 'Registered'}</span>
              {[
                { v: 'all', en: 'All time', ar: 'الكل' },
                { v: '7',   en: 'Last 7d',  ar: '7 أيام' },
                { v: '30',  en: 'Last 30d', ar: '30 يوم' },
                { v: '90',  en: 'Last 90d', ar: '90 يوم' },
              ].map(opt => (
                <button key={opt.v} type="button"
                  className={`admin-filter-chip${filterRegPeriod === opt.v ? ' active' : ''}`}
                  onClick={() => setFilterRegPeriod(opt.v)}>
                  {language === 'ar' ? opt.ar : opt.en}
                </button>
              ))}
            </div>

            <div className="admin-filter-group admin-filter-group--right">
              <label className="admin-toggle">
                <input
                  type="checkbox"
                  checked={showDeactivated}
                  onChange={(e) => setShowDeactivated(e.target.checked)}
                />
                <span>{language === 'ar' ? 'عرض المعطّلين' : 'Show deactivated'}</span>
              </label>
              <label className="admin-toggle">
                <input type="checkbox" checked={revealPhones} onChange={(e) => setRevealPhones(e.target.checked)} />
                <span>{language === 'ar' ? 'إظهار الهاتف' : 'Show phones'}</span>
              </label>
              {(filterRole !== 'all' || filterActivity !== 'all' || filterRegPeriod !== 'all' || kpiDrill || showDeactivated) && (
                <button type="button" className="admin-clear-drill" onClick={() => {
                  setFilterRole('all'); setFilterActivity('all'); setFilterRegPeriod('all'); setKpiDrill(null); setShowDeactivated(false);
                }}>
                  {language === 'ar' ? '✕ مسح الفلاتر' : '✕ Clear filters'}
                </button>
              )}
            </div>
          </div>
        </div>

        {(userSearchQuery.trim() || kpiDrill || filterRole !== 'all' || filterActivity !== 'all' || filterRegPeriod !== 'all' || !showDeactivated) && (
          <p className="admin-filter-hint">
            {language === 'ar'
              ? `عرض ${sortedDisplayUsers.length} صفاً — ${userStatusCounts.active} نشط (معتمد) · ${userStatusCounts.pending} قيد المراجعة · ${userStatusCounts.waitlist} قائمة انتظار · ${userStatusCounts.rejected} مرفوض · ${userStatusCounts.deactivated} معطّل — من ${users.length} في قاعدة البيانات${!showDeactivated ? ' (المعطّلون مخفيون)' : ''}`
              : `Showing ${sortedDisplayUsers.length} rows — ${userStatusCounts.active} active (approved) · ${userStatusCounts.pending} pending · ${userStatusCounts.waitlist} waitlist · ${userStatusCounts.rejected} rejected · ${userStatusCounts.deactivated} deactivated — of ${users.length} total${!showDeactivated ? ' (deactivated hidden)' : ''}`}
            {filterRole !== 'all' && ` · ${filterRole}`}
            {filterActivity === '1' && (language === 'ar' ? ' · نشط اليوم' : ' · active today')}
            {filterActivity === '7' && (language === 'ar' ? ' · نشط 7 أيام' : ' · active 7d')}
            {filterActivity === '30' && (language === 'ar' ? ' · نشط 30 يوم' : ' · active 30d')}
            {filterActivity === 'inactive30' && (language === 'ar' ? ' · غير نشط +30 يوم' : ' · inactive 30d+')}
            {filterRegPeriod !== 'all' && (language === 'ar' ? ` · مسجّل خلال ${filterRegPeriod} يوم` : ` · registered last ${filterRegPeriod}d`)}
          </p>
        )}

        {showAddUser && (
          <div className="add-user-form">
            <h3>{language === 'ar' ? 'مستخدم جديد' : 'New user'}</h3>
            <p className="add-user-form__hint">
              {language === 'ar'
                ? 'إذا كان رقم الهاتف لحساب معطّل سابقاً، سيتم إعادة التفعيل وتحديث الاسم وكلمة المرور.'
                : 'If this phone belongs to a previously deactivated account, we reactivate it and update name + password.'}
            </p>
            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label>{language === 'ar' ? 'الاسم' : 'Name'}</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{language === 'ar' ? 'الهاتف' : 'Phone'}</label>
                <input
                  type="tel"
                  value={newUser.phone_number}
                  onChange={(e) => setNewUser({ ...newUser, phone_number: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{language === 'ar' ? 'كلمة المرور' : 'Password'}</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  minLength={6}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="admin-button" disabled={loading}>
                  {language === 'ar' ? 'إنشاء / إعادة تفعيل' : 'Create / reactivate'}
                </button>
                <button type="button" onClick={() => setShowAddUser(false)} className="cancel-btn">
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="users-table-wrap">
          <table className="admin-data-table">
            <thead>
              <tr>
                <SortTh colKey="id" sortConfig={sortConfig} onSort={toggleSort} className="col-id" language={language}>
                  ID
                </SortTh>
                <SortTh colKey="name" sortConfig={sortConfig} onSort={toggleSort} language={language}>
                  {language === 'ar' ? 'الاسم' : 'Name'}
                </SortTh>
                <SortTh colKey="phone" sortConfig={sortConfig} onSort={toggleSort} className="col-phone" language={language}>
                  {language === 'ar' ? 'الهاتف' : 'Phone'}
                </SortTh>
                <SortTh colKey="role" sortConfig={sortConfig} onSort={toggleSort} className="col-role" language={language}>
                  {language === 'ar' ? 'الدور' : 'Role'}
                </SortTh>
                <SortTh colKey="tenant" sortConfig={sortConfig} onSort={toggleSort} className="col-org" language={language}>
                  <span className="th-main">{language === 'ar' ? 'المنشأة' : 'Tenant'}</span>
                  <span className="th-hint">{language === 'ar' ? '(مالك)' : '(owner)'}</span>
                </SortTh>
                <SortTh colKey="created_at" sortConfig={sortConfig} onSort={toggleSort} className="col-date" language={language}>
                  {language === 'ar' ? 'التسجيل' : 'Registered'}
                </SortTh>
                <SortTh colKey="last_visit" sortConfig={sortConfig} onSort={toggleSort} className="col-date" language={language}>
                  {language === 'ar' ? 'آخر زيارة' : 'Last visit'}
                </SortTh>
                <SortTh colKey="status" sortConfig={sortConfig} onSort={toggleSort} className="col-status" language={language}>
                  {language === 'ar' ? 'الحالة' : 'Status'}
                </SortTh>
                <th scope="col" className="col-actions admin-th-static">
                  {language === 'ar' ? 'إجراءات' : 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDisplayUsers.map((user) => {
                const isDeactivated = Boolean(user.deactivated_at);
                const accountStatus = getAdminRowAccountStatus(user);
                const rowClass = [
                  selectedUser === user.id ? 'selected' : '',
                  isDeactivated ? 'admin-row--deactivated' : '',
                  accountStatus === 'rejected' ? 'admin-row--rejected' : '',
                  accountStatus === 'waitlist' ? 'admin-row--waitlist' : '',
                  accountStatus === 'pending' ? 'admin-row--pending' : ''
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <tr key={user.id} className={rowClass}>
                    <td className="col-id mono">{user.id}</td>
                    <td className="cell-strong">{user.name}</td>
                    <td className="col-phone mono" title={revealPhones ? undefined : user.phone_number}>
                      {displayPhone(user.phone_number)}
                    </td>
                    <td className="col-role">
                      <span className="admin-role-pill">{user.role || 'owner'}</span>
                    </td>
                    <td className="col-org">
                      <OrgCell user={user} language={language} />
                    </td>
                    <td className="col-date muted">{formatAdminDate(user.created_at)}</td>
                    <td className="col-date muted">{formatAdminDate(user.last_visit_date)}</td>
                    <td className="col-status">
                      {accountStatus === 'deactivated' && (
                        <span className="admin-status-pill admin-status-pill--off" title={formatAdminDate(user.deactivated_at)}>
                          {language === 'ar' ? 'معطّل' : 'Deactivated'}
                        </span>
                      )}
                      {accountStatus === 'rejected' && (
                        <span className="admin-status-pill admin-status-pill--rejected" title={language === 'ar' ? 'تم رفض التسجيل' : 'Registration rejected'}>
                          {language === 'ar' ? 'مرفوض' : 'Rejected'}
                        </span>
                      )}
                      {accountStatus === 'pending' && (
                        <span className="admin-status-pill admin-status-pill--pending" title={language === 'ar' ? 'بانتظار موافقة الإدارة' : 'Pending admin approval'}>
                          {language === 'ar' ? 'قيد المراجعة' : 'Pending'}
                        </span>
                      )}
                      {accountStatus === 'waitlist' && (
                        <span className="admin-status-pill admin-status-pill--waitlist" title={language === 'ar' ? 'في قائمة الانتظار — يمكن الموافقة لاحقاً' : 'On waitlist — can approve later'}>
                          {language === 'ar' ? 'قائمة انتظار' : 'Waitlist'}
                        </span>
                      )}
                      {accountStatus === 'active' && (
                        <span className="admin-status-pill admin-status-pill--on" title={language === 'ar' ? 'حساب معتمد ويمكنه تسجيل الدخول' : 'Approved — can sign in'}>
                          {language === 'ar' ? 'نشط' : 'Active'}
                        </span>
                      )}
                    </td>
                    <td className="col-actions">
                    <div className="action-buttons">
                        <button type="button" onClick={() => loadUserStats(user.id)} className="btn-stats">
                        {language === 'ar' ? 'إحصائيات' : 'Stats'}
                      </button>
                        <button type="button" onClick={() => loadUserData(user.id)} className="btn-data">
                          {language === 'ar' ? 'بيانات' : 'Data'}
                      </button>
                        {accountStatus === 'rejected' ? (
                          <span
                            className="admin-action-muted"
                            title={language === 'ar' ? 'التسجيل مرفوض — لا يمكن تسجيل الدخول' : 'Registration rejected — cannot sign in'}
                          >
                            —
                          </span>
                        ) : accountStatus === 'waitlist' ? (
                          <button
                            type="button"
                            onClick={() => handleApproveUser(user.id, user.name)}
                            className="btn btn-success btn-approve-inline"
                          >
                            {language === 'ar' ? 'موافقة' : 'Approve'}
                          </button>
                        ) : isDeactivated ? (
                          <span
                            className="admin-action-muted"
                            title={language === 'ar' ? 'أعد التفعيل من «+ مستخدم» بنفس رقم الهاتف وكلمة مرور جديدة' : 'Reactivate via + Add user with same phone and new password'}
                          >
                            {language === 'ar' ? '+ مستخدم' : '+ Add user'}
                          </span>
                        ) : (
                          <button type="button" onClick={() => handleDeleteUser(user.id, user.name)} className="btn-delete">
                            {language === 'ar' ? 'تعطيل' : 'Deactivate'}
                      </button>
                        )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {adminTab === 'registration' && (
        <div
          className="admin-tab-panel admin-tab-panel--registration"
          role="tabpanel"
          id="admin-panel-registration"
          aria-labelledby="admin-tab-registration"
        >
          <div
            className="admin-reg-subtabs"
            role="tablist"
            aria-label={language === 'ar' ? 'أقسام التسجيل' : 'Registration subsections'}
          >
            <button
              type="button"
              role="tab"
              className={`admin-reg-subtab${regSubTab === 'settings' ? ' admin-reg-subtab--active' : ''}`}
              aria-selected={regSubTab === 'settings'}
              onClick={() => setRegSubTab('settings')}
            >
              {language === 'ar' ? 'إعدادات' : 'Settings'}
            </button>
            <button
              type="button"
              role="tab"
              className={`admin-reg-subtab${regSubTab === 'pending' ? ' admin-reg-subtab--active' : ''}`}
              aria-selected={regSubTab === 'pending'}
              onClick={() => setRegSubTab('pending')}
            >
              {language === 'ar' ? 'طلبات معلّقة' : 'Pending requests'}
              {(pendingReady.length > 0 || pendingAwaitingEmail.length > 0) && (
                <span className="admin-reg-subtab__badge">
                  {pendingReady.length > 0 ? pendingReady.length : pendingAwaitingEmail.length}
                </span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              className={`admin-reg-subtab${regSubTab === 'trail' ? ' admin-reg-subtab--active' : ''}`}
              aria-selected={regSubTab === 'trail'}
              onClick={() => setRegSubTab('trail')}
            >
              {language === 'ar' ? 'نماذج التجربة' : 'Trial forms'}
              {trailRegistrations.filter((r) => r && !r.exists_in_real).length > 0 && (
                <span className="admin-reg-subtab__badge">
                  {trailRegistrations.filter((r) => r && !r.exists_in_real).length}
                </span>
              )}
            </button>
      </div>

          {regSubTab === 'settings' && (
            <div className="admin-reg-panel">
              {regSettings ? (
                <section
                  className="admin-registration-rollout admin-registration-rollout--in-tab"
                  id="admin-registration-rollout"
                  aria-labelledby="reg-settings-heading"
                >
                  <div className="admin-registration-rollout__head">
                    <h2 id="reg-settings-heading" className="admin-section-title">
                      {language === 'ar' ? 'إعدادات التسجيل والإطلاق' : 'Registration & rollout settings'}
          </h2>
                    <p className="admin-registration-rollout__lead">
                      {language === 'ar'
                        ? 'فتح أو إغلاق التسجيل العام، رسائل الحظر، ومرحلة/جدول الإطلاق (مثل البرنامج التجريبي 28 أبريل).'
                        : 'Turn public sign-up on/off, closed messages, and rollout notes for a staged launch (e.g. trial 28 Apr).'}
                    </p>
            </div>
                  <form className="admin-registration-rollout__form" onSubmit={handleSaveRegistrationSettings}>
                    <label className="admin-reg-checkbox">
                      <input
                        type="checkbox"
                        checked={regSettings.public_signup_enabled !== false}
                        onChange={(e) => setRegSettings((s) => ({ ...s, public_signup_enabled: e.target.checked }))}
                      />
                      <span>
                        {language === 'ar'
                          ? 'السماح بالتسجيل الذاتي من صفحة «إنشاء حساب»'
                          : 'Allow public self‑registration (Create account)'}
                      </span>
                    </label>
                    <div className="admin-reg-grid">
                      <div className="form-group">
                        <label>
                          {language === 'ar' ? 'رسالة عند إغلاق التسجيل (عربي)' : 'Closed sign‑up message (Arabic)'}
                        </label>
                        <textarea
                          rows={2}
                          className="admin-reg-textarea"
                          value={regSettings.signup_closed_message_ar || ''}
                          onChange={(e) => setRegSettings((s) => ({ ...s, signup_closed_message_ar: e.target.value }))}
                        />
            </div>
                      <div className="form-group">
                        <label>
                          {language === 'ar' ? 'رسالة عند إغلاق التسجيل (إنجليزي)' : 'Closed sign‑up message (English)'}
                        </label>
                        <textarea
                          rows={2}
                          className="admin-reg-textarea"
                          value={regSettings.signup_closed_message_en || ''}
                          onChange={(e) => setRegSettings((s) => ({ ...s, signup_closed_message_en: e.target.value }))}
                        />
            </div>
            </div>
                    <div className="admin-reg-grid admin-reg-grid--2">
                      <div className="form-group">
                        <label>{language === 'ar' ? 'مرحلة الإطلاق' : 'Rollout phase'}</label>
                        <input
                          type="text"
                          className="admin-reg-input"
                          value={regSettings.rollout_phase || ''}
                          onChange={(e) => setRegSettings((s) => ({ ...s, rollout_phase: e.target.value }))}
                          placeholder={language === 'ar' ? 'مثال: تجريبي — دعوات فقط' : 'e.g. Pilot — invite only'}
                        />
            </div>
                      <div className="form-group">
                        <label>{language === 'ar' ? 'تاريخ مستهدف للإطلاق' : 'Target go‑live date'}</label>
                        <input
                          type="date"
                          className="admin-reg-input"
                          value={regSettings.target_go_live_at ? String(regSettings.target_go_live_at).slice(0, 10) : ''}
                          onChange={(e) => setRegSettings((s) => ({ ...s, target_go_live_at: e.target.value || null }))}
                        />
          </div>
                    </div>
                    <div className="form-group">
                      <label>{language === 'ar' ? 'ملاحظات داخلية' : 'Internal notes'}</label>
                      <textarea
                        rows={3}
                        className="admin-reg-textarea"
                        value={regSettings.internal_notes || ''}
                        onChange={(e) => setRegSettings((s) => ({ ...s, internal_notes: e.target.value }))}
                      />
                    </div>
                    <button type="submit" className="admin-reg-save" disabled={regSettingsSaving}>
                      {regSettingsSaving
                        ? language === 'ar'
                          ? 'جارٍ الحفظ…'
                          : 'Saving…'
                        : language === 'ar'
                          ? 'حفظ إعدادات التسجيل'
                          : 'Save registration settings'}
                    </button>
                  </form>
                </section>
              ) : (
                <p className="admin-panel-placeholder">
                  {language === 'ar' ? 'جارٍ تحميل إعدادات التسجيل…' : 'Loading registration settings…'}
                </p>
              )}
        </div>
      )}

          {regSubTab === 'pending' && (
            <div className="admin-reg-panel">
              {pendingRegTotal === 0 ? (
                <div className="admin-empty-panel" role="status">
                  <p className="admin-empty-panel__title">
                    {language === 'ar' ? 'لا طلبات تسجيل' : 'No registration activity'}
                  </p>
                  <p className="admin-empty-panel__text">
                    {language === 'ar'
                      ? 'عند التسجيل يظهر الطلب أولاً تحت «بانتظار تأكيد البريد». بعد فتح رابط التأكيد ينتقل إلى «جاهز للموافقة» لمراجعته.'
                      : 'After sign-up, requests appear under “Awaiting email verification”. Once the user clicks the verify link, they move to “Ready for approval” for your review.'}
                  </p>
                </div>
              ) : (
                <section className="pending-users-section pending-users-section--in-tab" aria-labelledby="pending-users-heading">
                  <div className="section-header">
                    <div>
                      <h2 id="pending-users-heading" className="admin-section-title">
                        {language === 'ar' ? 'طلبات التسجيل' : 'Registration requests'}
                      </h2>
                      <p className="pending-users-section__lead">
                        {language === 'ar'
                          ? '١) بانتظار تأكيد البريد — لم يُنشأ الحساب بعد. ٢) جاهز للموافقة — تم تأكيد البريد؛ راجع البيانات ثم وافق أو ارفض.'
                          : '1) Awaiting email — account not created yet. 2) Ready for approval — email verified; review details, then approve or reject.'}
                      </p>
                    </div>
                    <div className="section-header-info">
                      <label className="admin-reg-checkbox pending-users-reveal">
                        <input
                          type="checkbox"
                          checked={revealPhones}
                          onChange={(e) => setRevealPhones(e.target.checked)}
                        />
                        <span>{language === 'ar' ? 'إظهار أرقام الهواتف' : 'Reveal phone numbers'}</span>
                      </label>
                    </div>
                  </div>

                  {pendingAwaitingEmail.length > 0 && (
                    <div className="pending-reg-queue">
                      <div className="pending-reg-queue__head">
                        <h3 className="pending-reg-queue__title">
                          {language === 'ar' ? 'بانتظار تأكيد البريد' : 'Awaiting email verification'}
                        </h3>
                        <span className="pending-reg-queue__badge pending-reg-queue__badge--awaiting">
                          {pendingAwaitingEmail.length}
                        </span>
                      </div>
                      <p className="pending-reg-queue__hint">
                        {language === 'ar'
                          ? 'المستخدم لم يضغط رابط التأكيد بعد — لا توافق هنا؛ انتظر حتى ينتقل الطلب للقسم التالي.'
                          : 'User has not clicked the verification link yet — do not approve here; wait until the request moves to the next section.'}
                      </p>
                      {renderPendingUsersTable(pendingAwaitingEmail, {
                        queue: 'awaiting',
                        showActions: false,
                        statusPillClass: 'admin-status-pill--awaiting-email',
                        statusLabel: language === 'ar' ? 'بانتظار البريد' : 'Awaiting email',
                        dateLabel: language === 'ar' ? 'تاريخ الطلب' : 'Started',
                        getDateValue: (row) => formatAdminDate(row.created_at),
                      })}
                    </div>
                  )}

                  {pendingReady.length > 0 && (
                    <div className="pending-reg-queue">
                      <div className="pending-reg-queue__head">
                        <h3 className="pending-reg-queue__title">
                          {language === 'ar' ? 'جاهز للموافقة' : 'Ready for approval'}
                        </h3>
                        <span className="pending-reg-queue__badge pending-reg-queue__badge--ready">
                          {pendingReady.length}
                        </span>
                      </div>
                      <p className="pending-reg-queue__hint">
                        {language === 'ar'
                          ? 'موافقة = تفعيل فوري · قائمة انتظار = رسالة مهذّبة للمهتمين · رفض = سبام/وهمي بدون إشعار.'
                          : 'Approve = activate now · Waitlist = polite email to interested leads · Reject = spam/fake, no email.'}
                      </p>
                      {renderPendingUsersTable(pendingReady, {
                        queue: 'ready',
                        showActions: true,
                        statusPillClass: 'admin-status-pill--email-verified',
                        statusLabel: language === 'ar' ? 'البريد مؤكّد' : 'Email verified',
                        dateLabel: language === 'ar' ? 'تاريخ التأكيد' : 'Email verified',
                        getDateValue: (user) => formatAdminDate(user.email_verified_at || user.created_at),
                      })}
                    </div>
                  )}

                  {pendingLegacy.length > 0 && (
                    <div className="pending-reg-queue pending-reg-queue--legacy">
                      <div className="pending-reg-queue__head">
                        <h3 className="pending-reg-queue__title">
                          {language === 'ar' ? 'طلبات قديمة (بدون تأكيد بريد)' : 'Older requests (no email verification)'}
                        </h3>
                        <span className="pending-reg-queue__badge pending-reg-queue__badge--legacy">
                          {pendingLegacy.length}
                        </span>
                      </div>
                      <p className="pending-reg-queue__hint pending-reg-queue__hint--warn">
                        {language === 'ar'
                          ? 'تسجيلات قبل نظام تأكيد البريد — يُفضّل الرفض إن كانت البيانات غير موثوقة.'
                          : 'Sign-ups from before email verification — reject if details look unreliable.'}
                      </p>
                      {renderPendingUsersTable(pendingLegacy, {
                        queue: 'legacy',
                        showActions: true,
                        statusPillClass: 'admin-status-pill--legacy',
                        statusLabel: language === 'ar' ? 'بريد غير مؤكّد' : 'Email not verified',
                        dateLabel: language === 'ar' ? 'تاريخ الطلب' : 'Submitted',
                        getDateValue: (user) => formatAdminDate(user.created_at),
                      })}
                    </div>
                  )}
                </section>
              )}
            </div>
          )}

          {regSubTab === 'trail' && (
            <div className="admin-reg-panel">
              {trailRegistrations.length === 0 ? (
                <div className="admin-empty-panel" role="status">
                  <p className="admin-empty-panel__title">
                    {language === 'ar' ? 'لا بيانات من نماذج التجربة' : 'No trial form rows'}
                  </p>
                  <p className="admin-empty-panel__text">
                    {language === 'ar'
                      ? 'يُجلب الرد من جدول Google (تصدير CSV). تأكد أن الاستجابة في التبويب الصحيح، وأن الجدول «متاح لأي شخص لديه الرابط» إن لزم.'
                      : 'Responses are read from the linked Google Sheet (CSV export). Use the correct tab, and ensure the sheet is shared so the CSV URL is accessible if needed.'}
                  </p>
                  {trailFetchMeta &&
                    trailFetchMeta.csvDataRows > 0 &&
                    trailFetchMeta.parsedRows === 0 && (
                      <p className="admin-empty-panel__text admin-empty-panel__text--warn">
                        {language === 'ar'
                          ? 'السيرفر رأى صفوفاً في الملف لكنه لم يستطع استخراج الاسم ورقم الجوال — راجع ترتيب أعمدة النموذج أو أعد تسمية الرؤوس (مثل «الاسم»، «رقم الجوال»).'
                          : 'The server saw data rows but could not map name and phone — check your form’s column order or use clear header titles (e.g. name, phone).'}
                      </p>
                    )}
                </div>
              ) : (
                <section className="trail-registrations-section trail-registrations-section--in-tab" aria-labelledby="trail-registrations-heading">
                  <div className="section-header">
                    <h2 id="trail-registrations-heading" className="admin-section-title">
                      {language === 'ar' ? 'تسجيلات نموذج التجربة (Google Forms)' : 'Trail Registration Forms (Google Forms)'}
                    </h2>
                    <div className="section-header-info">
                      <span className="trail-count">
                        {trailRegistrations.length} {language === 'ar' ? 'تسجيل' : 'registrations'}
                      </span>
                      <span className="new-count">
                        {trailRegistrations.filter((r) => !r.exists_in_real).length} {language === 'ar' ? 'جديد' : 'new'}
                      </span>
                      <button
                        type="button"
                        onClick={handleBulkImport}
                        disabled={trailLoading || trailRegistrations.filter((r) => !r.exists_in_real).length === 0}
                        className="btn btn-primary bulk-import-btn"
                      >
                        {language === 'ar' ? 'استيراد الجميع' : 'Import All New'}
                      </button>
                    </div>
                  </div>
                  <div className="trail-registrations-grid">
                    {trailRegistrations.map((registration, index) => (
                      <div
                        key={index}
                        className={`trail-registration-card ${registration.exists_in_real ? 'already-imported' : 'new-registration'}`}
                      >
                        <div className="trail-registration-info">
                          <h3 className="trail-registration-name">{registration.name}</h3>
                          <p className="trail-registration-business">{registration.business_name}</p>
                          {registration.email && <p className="trail-registration-email">{registration.email}</p>}
                          <p className="trail-registration-phone">{maskPhone(registration.phone_number)}</p>
                          <div className="trail-registration-meta">
                            <span className="office-size">
                              {language === 'ar' ? 'الحجم:' : 'Size:'} {registration.office_size}
                            </span>
                            {registration.timestamp && (
                              <span className="trail-registration-date">{formatAdminDate(registration.timestamp)}</span>
                            )}
                          </div>
                        </div>
                        <div className="trail-registration-actions">
                          {registration.exists_in_real ? (
                            <div className="already-imported-status">
                              <span className="status-badge imported">
                                {language === 'ar' ? 'مستورد مسبقاً' : 'Already imported'}
                              </span>
                              {registration.existing_user_info && (
                                <small className="existing-info">
                                  ID: {registration.existing_user_info.id} • {registration.existing_user_info.registration_source}
                                </small>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleImportFromSheet(registration)}
                              disabled={trailLoading}
                              className="btn btn-success import-btn"
                            >
                              {language === 'ar' ? 'استيراد إلى Real' : 'Import to Real'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      )}

      {adminTab === 'whatsapp_ops' && (
        <section className="admin-whatsapp-ops" aria-labelledby="admin-wa-ops-heading" style={{ marginBottom: '28px' }}>
          <div className="section-header" style={{ alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <h2 id="admin-wa-ops-heading" className="admin-section-title">
                {language === 'ar' ? 'ملخص تشغيل واتساب (٢٤ ساعة)' : 'WhatsApp operations (24h)'}
              </h2>
              <p className="admin-detail-panel__lead" style={{ marginTop: '6px', maxWidth: '720px' }}>
                {language === 'ar'
                  ? 'ملخص داخل التطبيق للمسؤول عن المنصة: فشل الإرسال من صندوق المحادثات يختلف عن أخطاء جلب الوسائط (٤٠١/٥٠٢ في السجلات). راجع Render للتفاصيل.'
                  : 'In-app digest for the platform operator: inbox send failures differ from media fetch noise (401/502 in logs). Use Render logs for deep dives.'}
              </p>
            </div>
            <button
              type="button"
              className="admin-refresh-btn admin-refresh-btn--compact"
              disabled={whatsappOpsLoading}
              onClick={() => {
                const t = (sessionStorage.getItem('admin_token') || adminToken || '').trim();
                if (t) loadWhatsappOps(t);
              }}
            >
              {whatsappOpsLoading
                ? (language === 'ar' ? 'جاري التحميل…' : 'Loading…')
                : (language === 'ar' ? 'تحديث' : 'Refresh')}
            </button>
          </div>

          {whatsappOpsError && <div className="error-message" style={{ marginBottom: '12px' }}>{whatsappOpsError}</div>}

          {whatsappOpsLoading && !whatsappOps && (
            <p style={{ color: '#64748b', fontSize: '14px' }}>
              {language === 'ar' ? 'جاري تحميل البيانات…' : 'Loading operational data…'}
            </p>
          )}

          {whatsappOps && (
            <>
              <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>
                {language === 'ar' ? 'آخر تحديث: ' : 'Last updated: '}
                {formatAdminDate(whatsappOps.generatedAt)}
              </p>

              <div className="admin-detail-grid" style={{ marginBottom: '24px' }}>
                {[
                  {
                    v: whatsappOps.digest?.inboxAgentFailures24h ?? '—',
                    l: language === 'ar' ? 'فشل إرسال وكيل (٢٤س)' : 'Inbox agent send fails (24h)',
                  },
                  {
                    v: whatsappOps.digest?.broadcastDeliveryFailures24h ?? '—',
                    l: language === 'ar' ? 'أحداث فشل حملة (٢٤س)' : 'Broadcast fail events (24h)',
                  },
                  {
                    v: whatsappOps.digest?.disconnectedLines ?? '—',
                    l: language === 'ar' ? 'خطوط غير متصلة' : 'Disconnected lines',
                  },
                ].map((c) => (
                  <div key={c.l} className="admin-detail-tile">
                    <span className="admin-detail-tile__value">{c.v}</span>
                    <span className="admin-detail-tile__label">{c.l}</span>
                  </div>
                ))}
              </div>

              <h3 className="admin-section-title" style={{ fontSize: '1rem', marginBottom: '10px' }}>
                {language === 'ar' ? 'تنبيهات المسار الحرِج' : 'Critical-path alerts'}
              </h3>
              {whatsappOps.criticalAlerts && whatsappOps.criticalAlerts.length > 0 ? (
                <ul style={{ margin: '0 0 24px', paddingInlineStart: '20px', lineHeight: 1.5, color: '#0f172a' }}>
                  {whatsappOps.criticalAlerts.map((a, idx) => (
                    <li key={`${a.type}-${a.organizationId}-${idx}`} style={{ marginBottom: '8px' }}>
                      <strong>{a.title || a.organizationId}</strong>
                      <div style={{ fontSize: '13px', color: '#334155' }}>{a.detail}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: '14px' }}>
                  {language === 'ar' ? 'لا توجد تنبيهات حرجة ضمن القواعد الحالية.' : 'No critical alerts under current rules.'}
                </p>
              )}

              <h3 className="admin-section-title" style={{ fontSize: '1rem', marginBottom: '10px' }}>
                {language === 'ar' ? 'أعلى المنظمات — فشل إرسال الوكيل (٢٤س)' : 'Top orgs — inbox agent failures (24h)'}
              </h3>
              {whatsappOps.topOrgsByInboxFailures && whatsappOps.topOrgsByInboxFailures.length > 0 ? (
                <div className="users-table-wrap" style={{ marginBottom: '24px' }}>
                  <table className="admin-data-table">
                    <thead>
                      <tr>
                        <th>{language === 'ar' ? 'منظمة' : 'Tenant'}</th>
                        <th>{language === 'ar' ? 'معرّف' : 'Org id'}</th>
                        <th>{language === 'ar' ? 'الفشل' : 'Failures'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {whatsappOps.topOrgsByInboxFailures.map((row) => (
                        <tr key={row.organizationId}>
                          <td>{row.label}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{row.organizationId}</td>
                          <td>{row.inboxAgentFailures24h}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#64748b', fontSize: '14px' }}>
                  {language === 'ar' ? 'لا صفوف فشل وكيل في آخر ٢٤ ساعة.' : 'No agent send failures in the last 24 hours.'}
                </p>
              )}
            </>
          )}
        </section>
      )}

      {userStats && selectedUser && (
        <section className="admin-detail-panel user-stats-section">
          <h2 className="admin-detail-panel__title">
            {language === 'ar' ? 'إحصائيات الحساب' : 'Account statistics'} —{' '}
            {users.find((u) => u.id === selectedUser)?.name}
          </h2>
          <p className="admin-detail-panel__lead">
            {language === 'ar'
              ? 'أرقام لهذا المستخدم فقط — قارنها بإجماليات تبويب «لوحة التحكم».'
              : 'Counts for this user only — compare to totals on the Control center tab.'}
          </p>
          <div className="admin-detail-grid">
            {[
              { value: userStats.clients, label: language === 'ar' ? 'عملاء' : 'Clients' },
              { value: userStats.leads, label: language === 'ar' ? 'فرص' : 'Leads' },
              { value: userStats.activities, label: language === 'ar' ? 'أنشطة' : 'Activities' },
              { value: userStats.reminders, label: language === 'ar' ? 'تذكيرات' : 'Reminders' },
              { value: userStats.deals, label: language === 'ar' ? 'صفقات' : 'Deals' },
            ].map((c) => (
              <div key={c.label} className="admin-detail-tile">
                <span className="admin-detail-tile__value">{c.value}</span>
                <span className="admin-detail-tile__label">{c.label}</span>
            </div>
            ))}
          </div>
        </section>
      )}

      {userData && selectedUser && (
        <section className="admin-detail-panel user-data-section">
          <h2 className="admin-detail-panel__title">
            {language === 'ar' ? 'بيانات المستخدم' : 'User data'} — {userData.user.name}
          </h2>
          <div className="data-tabs">
            <div className="data-section">
              <h3>
                {language === 'ar' ? 'العملاء' : 'Clients'} ({userData.clients.length})
              </h3>
              <div className="data-list">
                {userData.clients.slice(0, 10).map((client) => (
                  <div key={client.id} className="data-item">
                    <strong>{client.name}</strong> — {client.phone || '—'}
                  </div>
                ))}
                {userData.clients.length > 10 && (
                  <div className="more-items">
                    {language === 'ar'
                      ? `+${userData.clients.length - 10}`
                      : `+${userData.clients.length - 10} more`}
                  </div>
                )}
              </div>
            </div>
            <div className="data-section">
              <h3>
                {language === 'ar' ? 'الفرص' : 'Leads'} ({userData.leads.length})
              </h3>
              <div className="data-list">
                {userData.leads.slice(0, 10).map((lead) => (
                  <div key={lead.id} className="data-item">
                    <strong>{lead.name}</strong> — {lead.phone || '—'}
                  </div>
                ))}
                {userData.leads.length > 10 && (
                  <div className="more-items">
                    {language === 'ar' ? `+${userData.leads.length - 10}` : `+${userData.leads.length - 10} more`}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminDashboard;
